import React, { useState, useEffect } from 'react';

// Time options from 8:00 AM to 10:00 PM in 30-minute intervals
const TIME_OPTIONS = [
  '08:00 AM', '08:30 AM', '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM',
  '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM',
  '05:00 PM', '05:30 PM', '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM',
  '08:00 PM', '08:30 PM', '09:00 PM', '09:30 PM', '10:00 PM'
];

const JobPostingPreview = ({ 
  isOpen, 
  onClose, 
  onProceed, 
  initialData = {}, 
  isDark 
}) => {
  const [formData, setFormData] = useState({
    companyName: '',
    salaryMin: '',
    salaryMax: '',
    allowCandidatesToCall: '',
    recruiterName: '',
    recruiterNumber: '',
    callStartTime: '09:00 AM',
    callEndTime: '06:00 PM',
    ...initialData
  });

  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        companyName: '',
        salaryMin: '',
        salaryMax: '',
        allowCandidatesToCall: '',
        recruiterName: '',
        recruiterNumber: '',
        callStartTime: '09:00 AM',
        callEndTime: '06:00 PM',
        ...initialData
      });
      setErrors({});
      setIsEditing(false);
    }
  }, [isOpen, initialData]);

  // Helper to convert time to comparable number (e.g., "09:30 AM" -> 930, "02:30 PM" -> 1430)
  const timeToNumber = (timeStr) => {
    if (!timeStr) return 0;
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let hour24 = hours;
    if (period === 'PM' && hours !== 12) hour24 += 12;
    if (period === 'AM' && hours === 12) hour24 = 0;
    return hour24 * 100 + minutes;
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'companyName':
        if (!value || !value.trim()) {
          error = 'Company name is required';
        } else if (/^\d+$/.test(value.trim())) {
          error = 'Company name cannot be only numbers';
        }
        break;
      
      case 'salaryMin':
        if (!value || isNaN(value) || parseInt(value) <= 0) {
          error = 'Please enter a valid minimum salary greater than 0';
        }
        break;
      
      case 'salaryMax':
        const minSalary = parseInt(formData.salaryMin);
        const maxSalary = parseInt(value);
        if (!value || isNaN(value) || maxSalary <= 0) {
          error = 'Please enter a valid maximum salary greater than 0';
        } else if (minSalary && maxSalary < minSalary) {
          error = `Maximum salary must be greater than or equal to minimum salary (₹${minSalary.toLocaleString()})`;
        }
        break;

      case 'allowCandidatesToCall':
        if (!value) {
          error = 'Please choose whether candidates can call you';
        }
        break;
      
      case 'recruiterNumber':
        if (formData.allowCandidatesToCall === 'Yes' && value && !/^\d{10}$/.test(value.replace(/[^\d]/g, ''))) {
          error = 'Please enter a valid 10-digit phone number';
        }
        break;
      
      default:
        break;
    }
    
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    
    ['companyName', 'salaryMin', 'salaryMax', 'allowCandidatesToCall'].forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    // Validate recruiter fields if allowing calls
    if (formData.allowCandidatesToCall === 'Yes') {
      if (!formData.recruiterName || !formData.recruiterName.trim()) {
        newErrors.recruiterName = 'Recruiter name is required when allowing candidates to call';
      } else {
        // Validate recruiter name format
        const nameStr = formData.recruiterName.trim();
        if (nameStr.length < 2) {
          newErrors.recruiterName = 'Recruiter name must be at least 2 characters';
        } else if (/^\d+$/.test(nameStr)) {
          newErrors.recruiterName = 'Recruiter name cannot be only numbers';
        } else if (!/^[A-Za-z\s.\-]+$/.test(nameStr)) {
          newErrors.recruiterName = 'Recruiter name should contain only letters, spaces, dots, and hyphens';
        }
      }
      if (!formData.recruiterNumber || !formData.recruiterNumber.trim()) {
        newErrors.recruiterNumber = 'Recruiter number is required when allowing candidates to call';
      } else {
        const phoneError = validateField('recruiterNumber', formData.recruiterNumber);
        if (phoneError) {
          newErrors.recruiterNumber = phoneError;
        } else {
          // Additional validation: Indian mobile numbers start with 6, 7, 8, or 9
          const cleanPhone = formData.recruiterNumber.replace(/[^\d]/g, '');
          if (cleanPhone.length === 10 && !'6789'.includes(cleanPhone[0])) {
            newErrors.recruiterNumber = 'Invalid mobile number. Indian mobile numbers start with 6, 7, 8, or 9';
          }
        }
      }
      // Validate call times
      if (formData.callStartTime && formData.callEndTime) {
        const startNum = timeToNumber(formData.callStartTime);
        const endNum = timeToNumber(formData.callEndTime);
        if (endNum <= startNum) {
          newErrors.callEndTime = 'End time must be greater than start time';
        }
      }
    }
    
    return newErrors;
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFieldBlur = (name) => {
    const error = validateField(name, formData[name]);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleProceedClick = () => {
    const validationErrors = validateAllFields();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsEditing(true);
      return;
    }
    
    onProceed(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div 
        className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
          isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-inherit">
          <h2 className="text-2xl font-semibold">Job Posting Preview</h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Please review and confirm the job posting details
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleFieldChange('companyName', e.target.value)}
                  onBlur={() => handleFieldBlur('companyName')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  } ${errors.companyName ? 'border-red-500' : ''}`}
                  placeholder="Enter company name"
                />
                {errors.companyName && (
                  <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                )}
              </>
            ) : (
              <p className={`px-3 py-2 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                {formData.companyName || <span className="text-gray-400 italic">Not provided</span>}
              </p>
            )}
          </div>

          {/* Salary Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Minimum Salary (₹) <span className="text-red-500">*</span>
              </label>
              {isEditing ? (
                <>
                  <input
                    type="number"
                    value={formData.salaryMin}
                    onChange={(e) => handleFieldChange('salaryMin', e.target.value)}
                    onBlur={() => handleFieldBlur('salaryMin')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    } ${errors.salaryMin ? 'border-red-500' : ''}`}
                    placeholder="e.g., 50000"
                  />
                  {errors.salaryMin && (
                    <p className="text-red-500 text-sm mt-1">{errors.salaryMin}</p>
                  )}
                </>
              ) : (
                <p className={`px-3 py-2 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  {formData.salaryMin ? `₹${parseInt(formData.salaryMin).toLocaleString()}` : <span className="text-gray-400 italic">Not provided</span>}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Maximum Salary (₹) <span className="text-red-500">*</span>
              </label>
              {isEditing ? (
                <>
                  <input
                    type="number"
                    value={formData.salaryMax}
                    onChange={(e) => handleFieldChange('salaryMax', e.target.value)}
                    onBlur={() => handleFieldBlur('salaryMax')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    } ${errors.salaryMax ? 'border-red-500' : ''}`}
                    placeholder="e.g., 80000"
                  />
                  {errors.salaryMax && (
                    <p className="text-red-500 text-sm mt-1">{errors.salaryMax}</p>
                  )}
                </>
              ) : (
                <p className={`px-3 py-2 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  {formData.salaryMax ? `₹${parseInt(formData.salaryMax).toLocaleString()}` : <span className="text-gray-400 italic">Not provided</span>}
                </p>
              )}
            </div>
          </div>

          {/* Allow Candidates to Call */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Allow candidates to call you? <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <>
                <select
                  value={formData.allowCandidatesToCall}
                  onChange={(e) => handleFieldChange('allowCandidatesToCall', e.target.value)}
                  onBlur={() => handleFieldBlur('allowCandidatesToCall')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                } ${errors.allowCandidatesToCall ? 'border-red-500' : ''}`}
                >
                  <option value="">Select an option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                {errors.allowCandidatesToCall && (
                  <p className="text-red-500 text-sm mt-1">{errors.allowCandidatesToCall}</p>
                )}
              </>
            ) : (
              <p className={`px-3 py-2 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                {formData.allowCandidatesToCall || <span className="text-gray-400 italic">Not selected</span>}
              </p>
            )}
          </div>

          {/* Recruiter Details (shown if allowing calls) */}
          {formData.allowCandidatesToCall === 'Yes' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Recruiter Name <span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={formData.recruiterName}
                      onChange={(e) => handleFieldChange('recruiterName', e.target.value)}
                      onBlur={() => handleFieldBlur('recruiterName')}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      } ${errors.recruiterName ? 'border-red-500' : ''}`}
                      placeholder="Enter recruiter name"
                    />
                    {errors.recruiterName && (
                      <p className="text-red-500 text-sm mt-1">{errors.recruiterName}</p>
                    )}
                  </>
                ) : (
                  <p className={`px-3 py-2 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {formData.recruiterName || <span className="text-gray-400 italic">Not provided</span>}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Recruiter Phone Number <span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <>
                    < input
                      type="tel"
                      value={formData.recruiterNumber}
                      onChange={(e) => handleFieldChange('recruiterNumber', e.target.value)}
                      onBlur={() => handleFieldBlur('recruiterNumber')}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      } ${errors.recruiterNumber ? 'border-red-500' : ''}`}
                      placeholder="Enter 10-digit phone number"
                    />
                    {errors.recruiterNumber && (
                      <p className="text-red-500 text-sm mt-1">{errors.recruiterNumber}</p>
                    )}
                  </>
                ) : (
                  <p className={`px-3 py-2 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    {formData.recruiterNumber || <span className="text-gray-400 italic">Not provided</span>}
                  </p>
                )}
              </div>
              
              {/* Call Time Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Call Start Time <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <select
                      value={formData.callStartTime}
                      onChange={(e) => handleFieldChange('callStartTime', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    >
                      {TIME_OPTIONS.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  ) : (
                    <p className={`px-3 py-2 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      {formData.callStartTime || '09:00 AM'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Call End Time <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <>
                      <select
                        value={formData.callEndTime}
                        onChange={(e) => handleFieldChange('callEndTime', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                        } ${errors.callEndTime ? 'border-red-500' : ''}`}
                      >
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                      {errors.callEndTime && (
                        <p className="text-red-500 text-sm mt-1">{errors.callEndTime}</p>
                      )}
                    </>
                  ) : (
                    <p className={`px-3 py-2 rounded-md ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      {formData.callEndTime || '06:00 PM'}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-inherit flex justify-end gap-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md transition-colors ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
            }`}
          >
            Cancel
          </button>
          {!isEditing ? (
            <>
              <button
                onClick={handleEditClick}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md transition-colors"
              >
                Edit
              </button>
              <button
                onClick={handleProceedClick}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-colors"
              >
                Proceed
              </button>
            </>
          ) : (
            <button
              onClick={handleProceedClick}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-colors"
            >
              Confirm & Proceed
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobPostingPreview;
